import React, { useState } from 'react';
import { QueryResult, ApolloQueryResult } from '@apollo/client';
import Form, { FormSection, FormHeader, FormFooter, Field } from '@atlaskit/form';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  successNotificationBuilder,
  ErrorComponents,
  AddressFormSection,
  SelectShipToAddress,
  shipToAddressesType,
  EditCreateInvoiceGroup,
  // getFeatureFlag,
} from '@css-xp/base';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import { updateInvoiceGroup } from '@atlassian/temp-proxies';
import {
  UpdateInvoiceGroupRequest,
  InvoiceGroupPurchaseOrder,
  InvoiceGroupWarnings,
} from '@atlassian/cssxp-types-billing';
import isNil from 'lodash/isNil';
import {
  CommerceAddress,
  FetchTransactionAccountQuery,
  FetchShipToPartiesForTransactionAccountQuery,
} from '@css-xp/graphtypes';
import { Checkbox } from '@atlaskit/checkbox';
// import { ENABLE_SAME_AS_BILL_TO } from '.../../../common/feature-flag-keys';
// import { getFeatureFlag } from '../../../common/security/selectors';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import Textfield from '@atlaskit/textfield';
import { fetchShippingAddressesForTransactionAccount } from '../../../../tabs/invoice-group-tabs';
import { fetchTransactionAccount } from '../../../../transaction-account';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';

interface FormData {
  shipToName: string;
  shipToAddress1?: string | null;
  shipToAddress2?: string | null;
  shipToCity?: string | null;
  shipToCountry?: { value: string; label: string };
  shipToPostalCode?: string | null;
  shipToState?: { value: string; label: string };
  shipToTaxId?: string | null;
  recipients: string[];
  invoiceGroupName: string;
  purchaseOrderNumber?: string | null;
}

interface Props {
  recipients?: string[];
  id: string;
  shipToParty?: CommerceAddress;

  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId: string;
  name?: string;
  purchaseOrder?: InvoiceGroupPurchaseOrder | null;
}
export const EditInvoiceGroupPane = ({
  recipients,
  id,
  refetch,
  shipToParty,
  transactionAccountId,
  name = '',
  purchaseOrder,
}: Props): JSX.Element => {
  const [adminFieldsDirty, setAdminsDirtyFlag] = React.useState<boolean>(true);
  const [transactionAccountPageResult, setTransactionAccountPageResult] = React.useState<
    Partial<QueryResult<FetchTransactionAccountQuery>>
  >({ loading: true });
  const [shipToAddresses, setShipToAddresses] = React.useState<
    Partial<QueryResult<FetchShipToPartiesForTransactionAccountQuery>>
  >({ loading: true });

  React.useEffect(() => {
    (async () => {
      const transactionAccountPageResult = await graphqlClient().query({
        query: fetchTransactionAccount,
        variables: { id: transactionAccountId },
        fetchPolicy: 'no-cache',
      });

      setTransactionAccountPageResult({ ...transactionAccountPageResult });
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      const shipToAddresses = await graphqlClient().query({
        query: fetchShippingAddressesForTransactionAccount,
        variables: { id: transactionAccountId },
        fetchPolicy: 'no-cache',
      });

      setShipToAddresses({ ...shipToAddresses });
    })();
  }, []);

  const { data, error } = transactionAccountPageResult;

  const sameAsBillToCheckBoxFeatureFlag = true;
  const isPONumberFeatureEnabled = true;

  const transactionAccount = data?.transactionAccount;
  const [isChecked, setIsChecked] = useState(false);
  const [showPOCheckbox, setShowPOCheckbox] = useState(purchaseOrder && purchaseOrder?.number ? true : false);
  const [isPOSingleUseChecked, setIsPOSingleUseChecked] = useState(purchaseOrder ? !purchaseOrder.oneTimeUse : false);
  const [shipToAddress, setShipToAddress] = React.useState<CommerceAddress | undefined | null>(undefined);
  const invoiceRecipients = !isNil(recipients) && recipients?.length === 0 ? [''] : recipients;
  const [existingShipToParty, setExistingShipToParty] = React.useState<CommerceAddress | undefined | null>(undefined);

  const { closeSidebar } = useSidebar();
  const oldShipTo = {
    shipToParty: {
      name: shipToParty?.name,
      taxId: shipToParty?.taxId === '' ? null : shipToParty?.taxId,
      postalAddress: {
        line1: shipToParty?.addressLine1,
        line2: shipToParty?.addressLine2,
        city: shipToParty?.city,
        country: shipToParty?.country,
        state: shipToParty?.state,
        postcode: shipToParty?.postcode,
      },
    },
  };

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateInvoiceGroupRequest, any>({
    submitFormAction: updateInvoiceGroup,
    onComplete: () => {
      closeSidebar();
      refetch();
    },
  });

  const onSelect = (selected: any) => {
    setExistingShipToParty(selected?.value);
    setIsChecked(false);
  };

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const updateAdminFieldsDirtyFlag = (flag: boolean) => {
    setAdminsDirtyFlag(flag);
  };

  const handleFormSubmit = (formData: FormData) => {
    let newShipTo;
    newShipTo = {
      shipToParty: {
        name: formData.shipToName,
        taxId: formData.shipToTaxId === '' ? null : formData.shipToTaxId,
        postalAddress: {
          line1: formData.shipToAddress1,
          line2: formData.shipToAddress2 === '' ? null : formData?.shipToAddress2,
          city: formData.shipToCity,
          country: formData.shipToCountry?.value,
          state: formData.shipToState?.value,
          postcode: formData.shipToPostalCode,
        },
      },
    };

    const existingShipTo = {
      shipToParty: {
        name: existingShipToParty?.name,
        taxId: existingShipToParty?.taxId,
        postalAddress: {
          line1: existingShipToParty?.addressLine1,
          line2: existingShipToParty?.addressLine2,
          city: existingShipToParty?.city,
          country: existingShipToParty?.country,
          state: existingShipToParty?.state,
          postcode: existingShipToParty?.postcode,
        },
      },
    };
    // compare the two shipTo's
    if (JSON.stringify(oldShipTo) === JSON.stringify(newShipTo)) {
      // shipToAddress didn't change. Don't send it in the request
      newShipTo = { shipToPartyId: shipToParty?.id ?? null };
    } else if (!isEmpty(existingShipToParty) && isEqual(existingShipTo, newShipTo)) {
      // also check if we selected an existing shipTo and not made any updates to it
      newShipTo = { shipToPartyId: existingShipToParty?.id ?? null };
    }

    const requestData: UpdateInvoiceGroupRequest = {
      data: {
        ...newShipTo,
        recipients: (formData?.recipients ?? []).filter((recipient: string) => recipient.length > 0),
        name: formData?.invoiceGroupName,
      },
      invoiceGroupId: id,
      transactionAccountId: transactionAccountId,
    };
    if (isPONumberFeatureEnabled && formData.purchaseOrderNumber) {
      requestData.data.purchaseOrder = {
        number: formData.purchaseOrderNumber,
        oneTimeUse: !isPOSingleUseChecked,
      };
    }

    submitForm(requestData);
  };

  const onSameAsBillTo = () => {
    // set the shipTo equal to our bill to address
    setShipToAddress(isChecked ? {} : transactionAccount?.billToParty);
    setIsChecked((current) => !current);
  };
  const onPOSingleUseChangeEvent = () => {
    setIsPOSingleUseChecked((current) => !current);
  };
  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('update-invoice-group-success')
      .withMessage('Successfully updated invoice group.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const newshipToParty = existingShipToParty ? existingShipToParty : shipToParty;
  const newShipToAddress = isChecked ? shipToAddress : newshipToParty;
  const purchaseOrderNumber = purchaseOrder && purchaseOrder.number ? purchaseOrder.number : '';

  const onPOTextChange = (value: string) => {
    if (value !== '') {
      setShowPOCheckbox(true);
    } else {
      setShowPOCheckbox(false);
    }
  };
  if (shipToAddresses?.loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  return (
    <div className={'invoice-group'} style={{ maxWidth: '600px', padding: '25px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, dirty, setFieldValue }) => (
          <form {...formProps}>
            <FormHeader title="Update invoice group"></FormHeader>
            <FormSection>
              {formError ? (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to update the invoice group for the following reason"
                      message={formError}
                    />
                  }
                />
              ) : !transactionAccountId ? (
                <InfoPanel
                  type="warning"
                  title={'Cannot update invoice group'}
                  message={'This invoice group is missing transaction-account id'}
                />
              ) : null}
            </FormSection>
            <FormSection>
              <div style={{ maxWidth: '500px' }}>
                <h3>Invoice group name</h3>
                <Field name="invoiceGroupName" id="invoiceGroupName" defaultValue={name} label="Name" isRequired={true}>
                  {({ fieldProps }) => (
                    <Textfield {...fieldProps} name="invoiceGroupName" testId="invoice-group-name" />
                  )}
                </Field>
              </div>
            </FormSection>
            <FormSection>
              <div style={{ maxWidth: '500px' }}>
                <h3 style={{ marginBottom: '10px' }}>Ship to address</h3>
                {sameAsBillToCheckBoxFeatureFlag && (
                  <Checkbox
                    isChecked={isChecked}
                    onChange={onSameAsBillTo}
                    label="Same as bill to address"
                    value="Controlled Checkbox"
                    name="controlled-checkbox"
                  />
                )}
                <SelectShipToAddress
                  onSelect={onSelect}
                  shipToAddresses={shipToAddresses?.data as shipToAddressesType}
                />
                <AddressFormSection
                  {...newShipToAddress}
                  isEditable={true}
                  addressName="Ship to"
                  setFieldValue={setFieldValue}
                  isRequired={true}
                />
              </div>
            </FormSection>
            <FormSection>
              {/* invoice-group can have 0 recipients and hence [''] to support '+' icon */}
              <EditCreateInvoiceGroup
                updateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                recipients={invoiceRecipients ?? ['']}
                isRequired={false}
                placeholder={'Email address'}
                helperMessage={'Add/remove or update the invoice contacts here'}
                title={'Invoice contacts'}
              />
            </FormSection>
            <FormSection>
              {isPONumberFeatureEnabled && (
                <div style={{ maxWidth: '500px' }}>
                  <h3>Purchase order</h3>
                  <Field
                    name="purchaseOrderNumber"
                    id="purchaseOrderNumber"
                    label="Number"
                    defaultValue={purchaseOrderNumber}
                  >
                    {({ fieldProps }) => (
                      <Textfield
                        {...fieldProps}
                        name="purchaseOrderNumber"
                        testId="purchase-order-number"
                        onChange={(event) => {
                          onPOTextChange(event.currentTarget.value);
                          fieldProps.onChange(event.currentTarget.value);
                        }}
                      />
                    )}
                  </Field>
                  <br />
                  {showPOCheckbox && (
                    <>
                      <Checkbox
                        isChecked={isPOSingleUseChecked}
                        onChange={onPOSingleUseChangeEvent}
                        label="Multiple Use PO Number"
                        value="Multiple Use PO Number"
                        name="multipleUse"
                        testId="po-multiple-use"
                      />
                      <InfoPanel type="warning" message={InvoiceGroupWarnings.PO_MULTIPLE_USE_WARNING} />
                    </>
                  )}
                </div>
              )}
            </FormSection>
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton
                  isDisabled={!dirty && !adminFieldsDirty}
                  appearance="primary"
                  type="submit"
                  testId="update-button"
                  isLoading={isProcessing}
                >
                  Save
                </LoadingButton>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default React.memo(EditInvoiceGroupPane);
